import { useMemo } from 'react';

import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import type { HeaderMotionMedia, HeaderMotionOptions } from '@shared/core/website/headerMotion/headerMotion.types';
import { isRecord } from '@shared/utils/isRecord';

import { motionRegistry } from './headerMotion.registry';
import type { HeaderMotionRegistry, HeaderMotionTypeDef } from './headerMotion.registry.types';

/** A Motion type ready to render, with its options sanitised and defaults filled in. */
export type ResolvedHeaderMotion = Readonly<{ def: HeaderMotionTypeDef; options: HeaderMotionOptions }>;

type EnabledMotion = Readonly<{ enabled: true; type: string; options?: unknown }>;

// The saved blob comes from the website JSON and is not schema-checked upstream, so anything
// short of a literal `enabled: true` (e.g. the string 'true') and a string `type` reads as off.
const isEnabledMotion = (value: unknown): value is EnabledMotion => isRecord(value) && value.enabled === true && typeof value.type === 'string';

/**
 * Turns a saved header `motion` blob into the Motion type to render, or `null` when none should.
 *
 * Returns `null` when the flag is off, the blob is missing/malformed/disabled, the type id is not
 * in `registry`, or the type does not support `media`. Options are parsed by the type's `parseOptions`
 * (unknown keys dropped, invalid values replaced) and laid over the type's defaults.
 *
 * Pure: never reads `window`, storage or the feature flag, so it is safe on the server and in tests.
 */
export const resolveHeaderMotion = (motion: unknown, media: HeaderMotionMedia, isFlagOn: boolean, registry: HeaderMotionRegistry = motionRegistry): ResolvedHeaderMotion | null => {
  if (!isFlagOn || !isEnabledMotion(motion)) {
    return null;
  }
  // Own-property check so ids like 'toString' cannot resolve to Object.prototype members.
  if (!Object.prototype.hasOwnProperty.call(registry, motion.type)) {
    return null;
  }
  const def = registry[motion.type];
  if (!def.supports.includes(media)) {
    return null;
  }
  const savedOptions = isRecord(motion.options) ? motion.options : {};
  return { def, options: { ...def.defaults, ...def.parseOptions(savedOptions) } };
};

/**
 * Hook form of `resolveHeaderMotion` that reads the `websiteHeaderMotionEnabled` flag. This is the
 * only place the guest site reads the flag for header Motion. Memoised so `options` keeps a stable
 * identity across re-renders while the inputs are unchanged.
 *
 * The flag is read with `skip` unless the header could show Motion with the flag on (enabled blob,
 * known type, supported media), so ordinary photo/video headers record no Amplitude exposure.
 */
export const useHeaderMotionConfig = (motion: unknown, media: HeaderMotionMedia): ResolvedHeaderMotion | null => useHeaderMotionConfigState(motion, media).resolved;

export type HeaderMotionConfigState = Readonly<{
  /** What `useHeaderMotionConfig` returns: `null` while loading, too. */
  resolved: ResolvedHeaderMotion | null;
  /**
   * True while the flag is still loading for a header that would show Motion with it on. Overlays can
   * ignore it (they simply appear once the flag settles), but a caller that changes the header's
   * structure on Motion (Showcase turns a Slider into a full-bleed header) must hold until it settles,
   * or the guest sees one header and then the other.
   */
  isLoading: boolean;
}>;

/** `useHeaderMotionConfig` plus whether the flag is still loading for this header. */
export const useHeaderMotionConfigState = (motion: unknown, media: HeaderMotionMedia): HeaderMotionConfigState => {
  // Eligibility uses the resolver's own checks with the flag assumed on, so the skip condition can
  // never drift from what would actually render.
  const isEligible = useMemo(() => resolveHeaderMotion(motion, media, true) !== null, [motion, media]);
  const [isFlagOn, isFlagLoading] = useIsFeatureEnabled('websiteHeaderMotionEnabled', { skip: !isEligible });
  const isLoading = isEligible && !!isFlagLoading;
  const resolved = useMemo(() => resolveHeaderMotion(motion, media, isFlagOn), [motion, media, isFlagOn]);
  return useMemo(() => ({ resolved: isLoading ? null : resolved, isLoading }), [resolved, isLoading]);
};
